import { useState } from "react";

import { useDrafts, usePendingReviews, useReviewDrafts } from "@/api/queries";
import { Button } from "@/components/ui/button";
import { Callout, ErrorNote } from "@/components/ui/panel";
import { formatDate } from "@/lib/utils";

/**
 * One generate run's questions, all ticked by default. Unticked ones are permanently deleted
 * when the batch is submitted — no row, no history — so the button asks twice and says so.
 * Keeping none is allowed and discards the whole batch.
 */
function Batch({ jobId }: { jobId: number }) {
  const drafts = useDrafts(jobId);
  const review = useReviewDrafts();
  const [unticked, setUnticked] = useState<ReadonlySet<number>>(new Set());
  const [confirming, setConfirming] = useState(false);

  const items = drafts.data?.items ?? [];
  const keepIds = items.filter((d) => !unticked.has(d.id)).map((d) => d.id);
  const discarding = items.length - keepIds.length;

  function toggle(id: number) {
    setConfirming(false);
    setUnticked((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  function setAll(keep: boolean) {
    setConfirming(false);
    setUnticked(keep ? new Set() : new Set(items.map((d) => d.id)));
  }

  function submit() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    review.mutate({ jobId, keepIds });
  }

  if (drafts.isLoading) return <p className="text-bank-muted text-xs">Loading…</p>;
  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setAll(true)}>
          Select all
        </Button>
        <Button size="sm" onClick={() => setAll(false)}>
          Select none
        </Button>
        <span className="text-bank-muted text-xs">
          {keepIds.length} of {items.length} selected
        </span>
      </div>

      <ul className="border-bank-border divide-bank-border max-h-96 divide-y overflow-y-auto rounded-md border">
        {items.map((d) => (
          <li key={d.id}>
            <label className="hover:bg-bank-surface-2 flex cursor-pointer items-start gap-2 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={!unticked.has(d.id)}
                onChange={() => toggle(d.id)}
                className="accent-bank-accent mt-1 size-3.5 shrink-0"
              />
              <span>{d.question_text}</span>
            </label>
          </li>
        ))}
      </ul>

      {confirming && (
        <Callout tone={discarding > 0 ? "bad" : "attention"}>
          {discarding === 0
            ? `All ${items.length} questions will go into the pipeline.`
            : `${discarding} unselected question${discarding === 1 ? "" : "s"} will be permanently deleted and cannot be recovered.`}{" "}
          Click again to confirm.
        </Callout>
      )}
      <ErrorNote error={review.error} />

      <Button
        variant={confirming && discarding > 0 ? "danger" : "primary"}
        disabled={review.isPending}
        onClick={submit}
      >
        {review.isPending
          ? "Saving…"
          : keepIds.length === 0
            ? `Delete all ${items.length} questions`
            : `Keep ${keepIds.length} selected, delete the rest`}
      </Button>

      {review.data && (
        <Callout tone="good">
          Kept {review.data.kept}, deleted {review.data.discarded}
          {review.data.literal_duplicates > 0
            ? `; ${review.data.literal_duplicates} matched a question already in the bank`
            : ""}
          .
        </Callout>
      )}
    </div>
  );
}

/**
 * Generate runs that finished but were not reviewed. Drafts never expire, so a batch left
 * overnight is still here; nothing from it is in the bank until a person keeps some.
 */
export function GeneratedReview() {
  const { data } = usePendingReviews();
  const [open, setOpen] = useState<number | null>(null);
  const items = data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <Callout>
        {items.length === 1 ? "A generated batch is" : `${items.length} generated batches are`} waiting for
        your review. Nothing enters the bank until you keep some.
      </Callout>
      {items.map((p) => (
        <div key={p.job_id} className="border-bank-border rounded-md border">
          <button
            type="button"
            onClick={() => setOpen(open === p.job_id ? null : p.job_id)}
            aria-expanded={open === p.job_id}
            className="hover:bg-bank-surface-2 flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm"
          >
            <span className="font-medium">
              Job #{p.job_id} · <code>{p.stack}</code> · {p.count} question{p.count === 1 ? "" : "s"}
            </span>
            <span className="text-bank-muted text-xs">
              {p.created_at ? formatDate(p.created_at) : ""} · {open === p.job_id ? "hide" : "review"}
            </span>
          </button>
          {open === p.job_id && (
            <div className="border-bank-border border-t p-3">
              <Batch jobId={p.job_id} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
